import { text } from './utils/fetch.mjs';
import Setting from './utils/setting.mjs';
import { registerHiddenSetting } from './share.mjs';
import setHeadend from './headend.mjs';

let playlist;
let currentTrack = 0;
let player;
let sliderTimeout = null;
let volumeSlider = null;
let volumeSliderInput = null;
// music was left on but the browser blocked playback until the page is interacted with
let waitingForInteraction = false;

const mediaPlaying = new Setting('mediaPlaying', {
	name: 'Media Playing',
	type: 'boolean',
	defaultValue: false,
	sticky: true,
});

document.addEventListener('DOMContentLoaded', () => {
	// #ToggleMedia is a pure play/stop toggle, independent of the volume popup
	document.getElementById('ToggleMedia').addEventListener('click', toggleMediaPlayback);

	// #ToggleMediaVolume opens/closes the volume popup, independent of playback.
	// it responds to click (so it works on touch/kiosk, which has no hover) and to hovering
	// the whole control cluster (a convenience for a mouse, not a requirement for anyone else)
	const volumeToggle = document.getElementById('ToggleMediaVolume');
	volumeToggle.addEventListener('click', toggleVolumeSlider);

	// get the slider elements
	volumeSlider = document.querySelector('#ToggleMediaContainer .volume-slider');
	volumeSliderInput = volumeSlider.querySelector('input');

	// catch interactions with the volume slider (timeout handler)
	// called on any interaction via 'input' (vs change) for immediate volume response
	volumeSlider.addEventListener('input', setSliderTimeout);
	volumeSlider.addEventListener('input', sliderChanged);
	// don't hide the slider while a keyboard user is on it
	volumeSlider.addEventListener('focusin', () => clearTimeout(sliderTimeout));
	volumeSlider.addEventListener('focusout', setSliderTimeout);

	// get the playlist
	getMedia();
});

const scanMusicDirectory = async () => {
	const parseDirectory = async (path, prefix = '') => {
		const listing = await text(path);
		const matches = [...listing.matchAll(/href="([^"]+\.mp3)"/gi)];
		return matches.map((m) => `${prefix}${m[1]}`);
	};

	try {
		let files = await parseDirectory('music/');
		if (files.length === 0) {
			files = await parseDirectory('music/default/', 'default/');
		}
		return { availableFiles: files };
	} catch (e) {
		console.error('Unable to scan music directory');
		console.error(e);
		return { availableFiles: [] };
	}
};

const getMedia = async () => {
	let playlistSource = '';

	try {
		// deliberate fetch (instead of safejson or others)
		// we need to examine the headers to determine the deployment mode and path forward
		const response = await fetch('playlist.json');
		if (response.ok) {
			playlist = await response.json();
			playlistSource = 'from server';
		} else if (response.status === 404 && response.headers.get('X-Weatherstar') === 'true') {
			// Expected behavior in static deployment mode
			playlist = await scanMusicDirectory();
			playlistSource = 'via directory scan (static deployment)';
		} else {
			playlist = { availableFiles: [] };
			playlistSource = `failed (${response.status} ${response.statusText})`;
		}
	} catch {
		// Network error or other fetch failure - fall back to directory scanning
		playlist = await scanMusicDirectory();
		playlistSource = 'via directory scan (after fetch failed)';
	}

	const fileCount = playlist?.availableFiles?.length || 0;
	if (fileCount > 0) {
		console.log(`Loaded playlist ${playlistSource} - found ${fileCount} music file${fileCount === 1 ? '' : 's'}`);
	} else {
		console.log(`No music files found ${playlistSource}`);
	}

	enableMediaPlayer();
};

const enableMediaPlayer = () => {
	// see if files are available
	if (playlist?.availableFiles?.length > 0) {
		// randomize the list
		randomizePlaylist();
		// enable the icon
		const icon = document.getElementById('ToggleMediaContainer');
		icon.classList.add('available');
		// set the button type
		setIcon();
		// if we're already playing (sticky option) then try to start playing
		if (mediaPlaying.value === true) {
			startMedia();
		}
	}
};

const setIcon = () => {
	// while waiting for an interaction the music is not heard, so the button shows it as off
	const playing = mediaPlaying.value === true && !waitingForInteraction;
	document.getElementById('ToggleMediaContainer').classList.toggle('playing', playing);
	document.getElementById('ToggleMedia').setAttribute('aria-pressed', playing);
};

// pure toggle: flips playback on/off and nothing else. Does not touch the volume popup.
const toggleMediaPlayback = () => {
	// music was left on and this click is the interaction the browser was waiting for, so start it
	if (waitingForInteraction) {
		stopWaitingForInteraction();
		startMedia();
		return;
	}
	mediaPlaying.value = !mediaPlaying.value;
	stateChanged();
};

// any click or key press on the page lets the browser start the music
const interactionStartsMedia = (e) => {
	// the music button's own click handler takes care of that button
	if (e.target?.closest?.('#ToggleMedia')) return;
	stopWaitingForInteraction();
	startMedia();
};

const waitForInteraction = () => {
	if (waitingForInteraction) return;
	waitingForInteraction = true;
	setIcon();
	setHeadend('music', 'Waiting for a click or key press to start');
	document.addEventListener('click', interactionStartsMedia, { capture: true });
	document.addEventListener('keydown', interactionStartsMedia, { capture: true });
};

const stopWaitingForInteraction = () => {
	waitingForInteraction = false;
	setIcon();
	document.removeEventListener('click', interactionStartsMedia, { capture: true });
	document.removeEventListener('keydown', interactionStartsMedia, { capture: true });
};

// open/close the volume popup. Independent of playback: volume can be adjusted whether or
// not music is currently playing.
const toggleVolumeSlider = () => {
	if (volumeSlider.classList.contains('show')) {
		hideVolumeSlider();
	} else {
		showVolumeSlider();
	}
};

// set a timeout for the volume slider (called by interactions with the slider)
const setSliderTimeout = () => {
	// clear existing timeout
	if (sliderTimeout) clearTimeout(sliderTimeout);
	// set a new timeout
	sliderTimeout = setTimeout(hideVolumeSlider, 5000);
};

// show the volume slider and configure a timeout
const showVolumeSlider = () => {
	setSliderTimeout();

	// show the slider
	if (volumeSlider) {
		volumeSlider.classList.add('show');
		document.getElementById('ToggleMediaVolume').setAttribute('aria-expanded', 'true');
	}
};

// hide the volume slider and clean up the timeout
const hideVolumeSlider = () => {
	// clear the timeout handler
	if (sliderTimeout) clearTimeout(sliderTimeout);
	sliderTimeout = null;

	// hide the element
	if (volumeSlider) {
		volumeSlider.classList.remove('show');
		document.getElementById('ToggleMediaVolume').setAttribute('aria-expanded', 'false');
	}
};

const startMedia = async () => {
	// if there's not media player yet, enable it
	if (!player) {
		initializePlayer();
	} else {
		try {
			await player.play();
			setTrackName(playlist.availableFiles[currentTrack]);
		} catch (e) {
			// the browser won't play sound before the page is interacted with, keep the saved setting and try again then
			if (e.name === 'NotAllowedError') {
				waitForInteraction();
				return;
			}
			// report the error
			console.error('Couldn\'t play music');
			console.error(e);
			// set state back to not playing for good UI experience
			mediaPlaying.value = false;
			stateChanged();
			setTrackName('Not playing');
		}
	}
};

const stopMedia = () => {
	hideVolumeSlider();
	if (!player) return;
	player.pause();
	mediaPlaying.value = false;
	setTrackName('Not playing');
	setIcon();
};

const stateChanged = () => {
	// update the icon
	setIcon();
	// react to the new state
	if (mediaPlaying.value) {
		startMedia();
	} else {
		stopMedia();
	}
};

const randomizePlaylist = () => {
	let availableFiles = [...playlist.availableFiles];
	const randomPlaylist = [];
	while (availableFiles.length > 0) {
		// get a randon item from the available files
		const i = Math.floor(Math.random() * availableFiles.length);
		// add it to the final list
		randomPlaylist.push(availableFiles[i]);
		// remove the file from the available files
		availableFiles = availableFiles.filter((file, index) => index !== i);
	}
	playlist.availableFiles = randomPlaylist;
};

const setVolume = (newVolume) => {
	if (player) {
		player.volume = newVolume;
	}
};

const sliderChanged = () => {
	// get the value of the slider
	if (volumeSlider) {
		const newValue = volumeSliderInput.value;
		const cleanValue = parseFloat(newValue) / 100;
		setVolume(cleanValue);
		mediaVolume.value = cleanValue;

		// interacting with the slider while stopped starts playback at the volume just set
		if (!mediaPlaying.value) {
			mediaPlaying.value = true;
			stateChanged();
		}
	}
};

const mediaVolume = new Setting('mediaVolume', {
	name: 'Volume',
	type: 'select',
	defaultValue: 0.75,
	values: [
		[1, '100%'],
		[0.75, '75%'],
		[0.50, '50%'],
		[0.25, '25%'],
	],
	changeAction: setVolume,
	visible: false,
});

registerHiddenSetting('mediaVolume', mediaVolume);

const initializePlayer = () => {
	// basic sanity checks
	if (!playlist.availableFiles || playlist?.availableFiles.length === 0) {
		throw new Error('No playlist available');
	}
	if (player) {
		return;
	}
	// create the player
	player = new Audio();

	// reset the playlist index
	currentTrack = 0;

	// add event handlers
	player.addEventListener('canplay', playerCanPlay);
	player.addEventListener('ended', playerEnded);

	// get the first file
	player.src = `music/${playlist.availableFiles[currentTrack]}`;
	setTrackName(playlist.availableFiles[currentTrack]);
	player.type = 'audio/mpeg';
	// set volume and slider indicator
	setVolume(mediaVolume.value);
	volumeSliderInput.value = Math.round(mediaVolume.value * 100);
};

const playerCanPlay = async () => {
	// check to make sure they user still wants music (protect against slow loading music)
	if (!mediaPlaying.value) return;
	// start playing
	startMedia();
};

const playerEnded = () => {
	// next track
	currentTrack += 1;
	// roll over and re-randomize the tracks
	if (currentTrack >= playlist.availableFiles.length) {
		randomizePlaylist();
		currentTrack = 0;
	}
	// update the player source
	player.src = `music/${playlist.availableFiles[currentTrack]}`;
	setTrackName(playlist.availableFiles[currentTrack]);
};

const setTrackName = (fileName) => {
	// check for metadata
	const metaData = playlist?.metaData?.[fileName];

	// generate a fallback track name from the file name
	const baseName = fileName.split('/').pop();
	const fileTrackName = decodeURIComponent(
		baseName.replace(/\.mp3/gi, '').replace(/(_-)/gi, ''),
	);
	if (!metaData) {
		setHeadend('music', fileTrackName);
		return;
	}

	// otherwise build a name from metadata
	let trackName = metaData.title ?? fileTrackName;
	if (metaData?.artist) trackName += ` - ${metaData.artist}`;
	if (metaData?.album) trackName += ` - ${metaData.album}`;
	// min:sec format
	const d = metaData?.duration;
	if (d) trackName += ` (${Math.floor(d / 60)}:${String(Math.floor(d % 60)).padStart(2, '0')})`;

	setHeadend('music', trackName);
};

export default toggleMediaPlayback;
